import React, { useEffect, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { BsPlusSquare } from "react-icons/bs";
import { CiSaveUp2 } from "react-icons/ci";

const InstallApp: React.FC = () => {
  const [isInstalled, setIsInstalled] = useState(false);
  const [, setIsIos] = useState(false);
  const [showIosBanner, setShowIosBanner] = useState(false);
  const [showAndroidBanner, setShowAndroidBanner] = useState(false);
  const [installPrompt, setInstallPrompt] = useState<Event | null>(null);
  const [showSecondBanner, setShowSecondBanner] = useState(false);

  const handleCloseSecondBanner = () => {
    setShowSecondBanner(false);
  };

  useEffect(() => {
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isIosDevice =
      /iphone|ipad|ipod/.test(userAgent) &&
      !window.matchMedia("(display-mode: standalone)").matches;
    const isAndroidDevice = /android/.test(userAgent);

    setIsIos(isIosDevice);
    setShowIosBanner(isIosDevice);
    setShowAndroidBanner(isAndroidDevice);
  }, []);

  useEffect(() => {
    const isStandalone = window.matchMedia(
      "(display-mode: standalone)"
    ).matches;
    const isIOSStandalone = (window.navigator as any).standalone;

    setIsInstalled(Boolean(isStandalone || isIOSStandalone));
  }, []);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt
      );
    };
  }, []);

  const handleInstall = () => {
    if (installPrompt) {
      (installPrompt as any).prompt();
      (installPrompt as any).userChoice.then(
        (choiceResult: { outcome: string }) => {
          if (choiceResult.outcome === "accepted") {
            console.log("User accepted the install prompt");
          } else {
            console.log("User dismissed the install prompt");
          }
          // Do not set installPrompt to null to allow the banner to be shown again
        }
      );
    }
  };

  const handleCloseIosBanner = () => {
    setShowIosBanner(false);
  };

  const handleCloseAndroidBanner = () => {
    setShowAndroidBanner(false);
  };

  return (
    <div className="bg-gray-100 relative">
      {showIosBanner && !isInstalled && (
        <div>
          <div className="fixed inset-x-0 mt-[85px] z-[50] bg-gray-800 text-white p-2 flex flex-col items-center justify-between shadow-lg animate-slideIn">
            <div className="flex items-center justify-center w-full mb-4">
              <div className="text-lg font-semibold text-center">
                App is available to install
              </div>
            </div>

            <div className="flex items-center justify-center w-full mb-2">
              <button
                onClick={() => setShowSecondBanner(true)}
                className="bg-green-500 hover:bg-green-600 text-white py-1 px-3 rounded font-semibold transition-all duration-300 ease-in-out"
              >
                Show Instructions
              </button>
            </div>

            <button
              onClick={handleCloseIosBanner}
              className="absolute top-2 right-2 text-white hover:text-gray-200"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {showSecondBanner && (
            <div className="fixed inset-x-0 mt-[86px] bg-gray-800 text-white p-2 flex flex-col items-center justify-between shadow-lg z-[101] animate-slideIn">
              <div className="flex items-center justify-center w-full mb-2">
                <div className="text-sm flex items-center">
                  <CiSaveUp2 className="inline w-8 h-8 text-white mb-3 mr-2" />
                  Click On Share
                </div>
              </div>

              <div className="flex items-center justify-center w-full mb-2">
                <div className="flex items-center">
                  Add To Home Screen
                  <BsPlusSquare className="inline w-6 h-6 text-white ml-2 mt-1" />
                </div>
              </div>

              <button
                onClick={handleCloseSecondBanner}
                className="absolute top-2 right-2 text-white hover:text-gray-200"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
          )}
        </div>
      )}

      {showAndroidBanner && !isInstalled && (
        <div className="fixed inset-x-0 mt-[80px] bg-gray-800 z-[50] text-white p-4 flex items-center justify-between animate-slideIn">
          <p className="text-sm">Install this app for a better experience.</p>
          <button
            onClick={handleInstall}
            className="bg-green-500 hover:bg-green-600 text-white px-2 py-1 rounded-lg transition-all duration-300 ease-in-out"
          >
            Install
          </button>
          <button
            onClick={handleCloseAndroidBanner}
            className="text-gray-400 hover:text-gray-200 ml-4"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>
      )}
    </div>
  );
};

export default InstallApp;
